// Per-route metadata: every page unique (docs/DESIGN of the outside world).
// Applied on route change; JSON-LD structured data per page type.

interface PageMeta {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  jsonLd?: Record<string, unknown>;
}

const SITE = "https://www.beatdraft.com";
const SUFFIX = " · Beat Draft";

export const routeMeta: Record<string, Omit<PageMeta, "path">> = {
  "/": {
    title: "Beat Draft. The home of living music.",
    description:
      "Music should never be finished. It should evolve. Type what you feel and hear your idea as living, editable music in seconds.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Beat Draft",
      applicationCategory: "MusicApplication",
      operatingSystem: "Web",
      description: "The home of living music. Create, jam, publish and grow a musical universe.",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    },
  },
  "/universe": { title: "Your universe" + SUFFIX, description: "Everything you have ever made, connected and alive." },
  "/discover": { title: "Discover" + SUFFIX, description: "Living songs from every universe: play them, fork them, build on them." },
  "/marketplace": { title: "Marketplace" + SUFFIX, description: "Packs, instruments, chains and Rhodes personalities made by creators." },
  "/learn": { title: "Learn" + SUFFIX, description: "Music production, taught by making. Lessons, challenges and Rhodes beside you." },
  "/reflections": { title: "Reflections" + SUFFIX, description: "Your creative story: milestones, recaps and your year in music." },
  "/about": { title: "About" + SUFFIX, description: "Why we believe music should never be finished." },
  "/trust": { title: "Trust Center" + SUFFIX, description: "Security, privacy and the commitments behind Beat Draft." },
};

export function applyMeta(path: string, dynamic?: Partial<PageMeta>): void {
  const base = routeMeta[path] ?? routeMeta["/"];
  const title = dynamic?.title ?? base.title;
  const description = dynamic?.description ?? base.description;
  const url = SITE + (dynamic?.path ?? path);

  document.title = title;
  setMeta("description", description);
  setProp("og:title", title);
  setProp("og:description", description);
  setProp("og:url", url);
  setProp("og:image", dynamic?.ogImage ?? `${SITE}/og.png`);
  setProp("og:type", "website");
  setProp("og:site_name", "Beat Draft");
  setMeta("twitter:card", "summary_large_image");
  setCanonical(url);
  setJsonLd(dynamic?.jsonLd ?? base.jsonLd);
}

function setMeta(name: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.name = name;
    document.head.appendChild(el);
  }
  el.content = content;
}

function setProp(property: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setCanonical(url: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = url;
}

function setJsonLd(data?: Record<string, unknown>): void {
  document.getElementById("bd-jsonld")?.remove();
  if (!data) return;
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.id = "bd-jsonld";
  s.textContent = JSON.stringify(data);
  document.head.appendChild(s);
}

/** JSON-LD for a published Session page (MusicRecording with provenance). */
export function sessionJsonLd(s: { name: string; creator: string; datePublished: string; url: string }): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "MusicRecording",
    name: s.name,
    byArtist: { "@type": "Person", name: s.creator },
    datePublished: s.datePublished,
    url: s.url,
  };
}

/** JSON-LD for a blog article: Article + Person (author) + BreadcrumbList,
 *  emitted as one @graph so a single script tag carries all three. */
export function articleJsonLd(a: {
  title: string;
  description: string;
  author: string;
  datePublished: string;
  slug: string;
  image?: string;
}): Record<string, unknown> {
  const url = `${SITE}/blog/${a.slug}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: a.title,
        description: a.description,
        image: a.image ?? `${SITE}/og.png`,
        datePublished: a.datePublished,
        dateModified: a.datePublished,
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        url,
        author: { "@type": "Organization", name: a.author, url: `${SITE}/company/about` },
        publisher: {
          "@type": "Organization",
          name: "Beat Draft",
          logo: { "@type": "ImageObject", url: `${SITE}/og.png` },
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Blog", item: `${SITE}/blog` },
          { "@type": "ListItem", position: 2, name: a.title, item: url },
        ],
      },
    ],
  };
}
